import { useEffect, useState } from 'react';
import type { ComponentType, JSX } from 'react';
import { IconModule, clefDIcone } from '@taki/ui';

/**
 * L'ICONE D'UNE APP DECLAREE, DERIVEE DE SA FICHE.
 *
 * `fiche.icon` est un NOM DE COMPOSANT (`IconContactBook`), alors que le
 * registre complet des icones est en clefs kebab et arrive en import DYNAMIQUE
 * (des centaines de kilo-octets, jamais dans le bundle). D'ou un composant et
 * non un noeud fige : a froid il rend `IconModule`, puis le vrai dessin des que
 * le registre est la. C'est exactement ce que le shell fait pour le dock et le
 * hub (`resolveAppEntry`, `packages/shell/src/appRegistry.ts`).
 *
 * POURQUOI CE N'EST PAS LE MEME CODE QUE LE SHELL. `@taki/app-runtime` ne depend
 * pas de `@taki/shell` (l'ordre de build est ui -> app-runtime -> shell), donc
 * son chargeur paresseux n'est pas accessible d'ici. Ce qui est ecrit deux fois,
 * ce sont cinq lignes de memoisation, jamais le registre : c'est un module ES,
 * le navigateur ne le charge qu'une fois quel que soit le nombre d'`import()`
 * qui le demandent.
 *
 * UN NOM QUE LE REGISTRE NE PORTE PAS REND L'ICONE NEUTRE, sans erreur :
 * `pnpm apps:check` refuse d'abord une fiche qui en porte un, et une app sans
 * dessin vaut mieux qu'un ecran qui ne monte pas.
 */

/** Le registre complet et son composant generique, charges ensemble. */
interface RegistreCharge {
  icones: Record<string, string>;
  Icone: ComponentType<{ name: string; size?: number }>;
}

/** Cache de MODULE : une seule requete par session, partagee par toutes les apps. */
let cache: RegistreCharge | null = null;
let enVol: Promise<RegistreCharge> | null = null;

function chargerLeRegistre(): Promise<RegistreCharge> {
  enVol ??= import('@taki/ui/icon-registry').then((module) => {
    const charge: RegistreCharge = { icones: module.iconRegistry, Icone: module.Icon };
    cache = charge;
    return charge;
  });
  return enVol;
}

export function IconeDeFiche({ nom, size = 22 }: { nom: string; size?: number }): JSX.Element {
  const [registre, setRegistre] = useState(cache);

  useEffect(() => {
    if (registre !== null) return;
    let vivant = true;
    void chargerLeRegistre().then((charge) => {
      if (vivant) setRegistre(charge);
    });
    return () => {
      vivant = false;
    };
  }, [registre]);

  if (registre === null) return <IconModule size={size} />;
  const clef = clefDIcone(nom, registre.icones);
  if (clef === '') return <IconModule size={size} />;
  const Icone = registre.Icone;
  return <Icone name={clef} size={size} />;
}
